import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createOrder } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';
import { ShoppingCart, Package, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import ProPaywall from '@/components/ProPaywall';

const OrderForm = ({ product, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [formData, setFormData] = useState({
    quantity: 1,
    unit: 'metros',
    shipping_address: '',
    comments: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.shipping_address.trim()) {
      toast.error('Ingresa la dirección de envío');
      return;
    }

    if (formData.quantity < 1) {
      toast.error('La cantidad debe ser al menos 1');
      return;
    }

    setLoading(true);
    try {
      await createOrder({
        product_id: product.product_id,
        quantity: parseInt(formData.quantity),
        unit: formData.unit,
        shipping_address: formData.shipping_address,
        comments: formData.comments || null
      });
      toast.success('¡Pedido realizado exitosamente!');
      onClose();
      navigate('/dashboard/orders');
    } catch (error) {
      if (error.status === 403) {
        onClose();
        setShowPaywall(true);
      } else {
        toast.error(error.message || 'Error al realizar el pedido');
      }
    } finally {
      setLoading(false);
    }
  };

  const totalPrice = product.price_per_unit 
    ? product.price_per_unit * formData.quantity 
    : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" style={{ color: '#1E4C8E' }} />
            Realizar pedido
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Product Info */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            {product.images && product.images.length > 0 ? (
              <img 
                src={getFirstImage(product.images)} 
                alt={product.name}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-slate-400" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-slate-900">{product.name}</h3>
              <p className="text-sm text-slate-500">
                {product.price_per_unit 
                  ? `$${product.price_per_unit.toLocaleString('es-MX')} / ${product.unit || 'unidad'}`
                  : 'Precio a cotizar'}
              </p>
            </div>
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label htmlFor="quantity">Cantidad</Label>
            <div className="flex gap-2">
              <Input
                id="quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
                className="flex-1"
              />
              <Select
                value={formData.unit}
                onValueChange={(value) => setFormData({ ...formData, unit: value })}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="metros">Metros</SelectItem>
                  <SelectItem value="kilos">Kilos</SelectItem>
                  <SelectItem value="piezas">Piezas</SelectItem>
                  <SelectItem value="rollos">Rollos</SelectItem>
                  <SelectItem value="yardas">Yardas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {product.min_order && (
              <p className="text-xs text-slate-500">Pedido mínimo: {product.min_order}</p>
            )}
          </div>

          {/* Shipping Address */}
          <div className="space-y-2">
            <Label htmlFor="shipping_address" className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              Dirección de envío
            </Label>
            <Textarea
              id="shipping_address"
              placeholder="Calle, número, colonia, ciudad, estado, CP"
              value={formData.shipping_address}
              onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
              rows={2}
              required
            />
          </div>

          {/* Comments */}
          <div className="space-y-2">
            <Label htmlFor="comments">Comentarios (opcional)</Label>
            <Textarea
              id="comments"
              placeholder="Instrucciones especiales, especificaciones, etc."
              value={formData.comments}
              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
              rows={2}
            />
          </div>

          {/* Total */}
          {totalPrice && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Total estimado:</span>
              <span className="text-xl font-bold" style={{ color: '#1E4C8E' }}>
                ${totalPrice.toLocaleString('es-MX')} MXN
              </span>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              style={{ backgroundColor: '#34B772' }}
              className="hover:opacity-90"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Confirmar pedido
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Pro Paywall Modal */}
      <ProPaywall 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        feature="realizar pedidos"
      />
    </Dialog>
  );
};

export default OrderForm;
